import type {ReactElement} from 'react';
import React from 'react';
import {BaseItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-shadow-option';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {ItemShadowProperties} from '@PosterWhiteboard/classes/item-aura.class';
import type {TranscriptShadowOptionProps} from './transcript-shadow-option.types';
import type {RGBA} from '@Utils/color.util';
import {useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem';

export function TranscriptShadowOption({...props}: TranscriptShadowOptionProps): ReactElement {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptShadowOption');
  }

  const activeTranscriptItemAuraType = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('type');
  if (activeTranscriptItemAuraType === undefined) {
    throw new Error('Transcript item subtitles do not have a type property in their aura object');
  }

  const activeTranscriptItemglowColor = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('glowColor');
  if (activeTranscriptItemglowColor === undefined) {
    throw new Error('Transcript item subtitles do not have a glowColor property in their aura object');
  }

  const activeTranscriptItemDropShadowAngle = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('dropShadowAngle');
  if (activeTranscriptItemDropShadowAngle === undefined) {
    throw new Error('Transcript item subtitles do not have a dropShadowAngle property in their aura object');
  }

  const activeTranscriptItemDropShadowColor = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('dropShadowColor');
  if (activeTranscriptItemDropShadowColor === undefined) {
    throw new Error('Transcript item subtitles do not have a dropShadowColor property in their aura object');
  }

  const activeTranscriptItemDropShadowDistance = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('dropShadowDistance');
  if (activeTranscriptItemDropShadowDistance === undefined) {
    throw new Error('Transcript item subtitles do not have a dropShadowDistance property in their aura object');
  }

  const activeTranscriptItemDropShadowBlur = useGetKeyFromSubtitleAuraPropertiesForActiveTranscriptItem('dropShadowBlur');
  if (activeTranscriptItemDropShadowBlur === undefined) {
    throw new Error('Transcript item subtitles do not have a dropShadowBlur property in their aura object');
  }

  const updateShadowObject = async (newProperties: ItemShadowProperties): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      aura: newProperties,
    });
  };

  const updateOpacity = async (dropShadowAlpha: number, undoable = true): Promise<void> => {
    const dropShadowColor = activeItem.getAnySubtitle().aura?.dropShadowColor?.slice() as unknown as RGBA;
    dropShadowColor[3] = dropShadowAlpha;

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        aura: {
          dropShadowColor,
        },
      },
      undoable
    );
  };

  const updateAngle = async (dropShadowAngle: number, undoable = true): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        aura: {
          dropShadowAngle,
        },
      },
      undoable
    );
  };

  const updateDistance = async (dropShadowDistance: number, undoable = true): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        aura: {
          dropShadowDistance,
        },
      },
      undoable
    );
  };

  const updateBlur = async (dropShadowBlur: number, undoable = true): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        aura: {
          dropShadowBlur,
        },
      },
      undoable
    );
  };

  return (
    <BaseItemShadowOption
      onClickColorFill={(): void => {
        props.onTranscriptShadowColorFillClick();
      }}
      onToggleShadow={updateShadowObject}
      auraObject={{
        type: activeTranscriptItemAuraType,
        glowColor: activeTranscriptItemglowColor,
        dropShadowAngle: activeTranscriptItemDropShadowAngle,
        dropShadowBlur: activeTranscriptItemDropShadowBlur,
        dropShadowDistance: activeTranscriptItemDropShadowDistance,
        dropShadowColor: activeTranscriptItemDropShadowColor,
      }}
      itemType={activeItem.gitype}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
    />
  );
}
